// src/services/validationRulesService.js
import axios from 'axios';

// Adaptez cette URL selon votre configuration
// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5282/api';
const API_URL = 'https://resolvebo.innovationcode.pro/api';

const validationRulesService = {
    /**
     * Récupère toutes les règles de validation
     * @returns {Promise<Array>} Les règles de validation
     */
    getAllRules: async () => {
        try {
            const response = await axios.get(`${API_URL}/ValidationRules`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des règles de validation:', error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Récupère les règles de validation actives
     * @returns {Promise<Array>} Les règles de validation actives
     */
    getActiveRules: async () => {
        try {
            const response = await axios.get(`${API_URL}/ValidationRules/active`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des règles de validation actives:', error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Récupère une règle de validation par son ID
     * @param {number} id - L'ID de la règle
     * @returns {Promise<Object>} La règle de validation
     */
    getRuleById: async (id) => {
        try {
            const response = await axios.get(`${API_URL}/ValidationRules/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Erreur lors de la récupération de la règle de validation ${id}:`, error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Crée une nouvelle règle de validation
     * @param {Object} rule - La règle à créer
     * @returns {Promise<Object>} La règle créée
     */
    createRule: async (rule) => {
        try {
            // Préparer la règle pour l'envoi
            const preparedRule = prepareRuleForSubmission(rule);
            console.log("Données préparées pour création:", preparedRule);

            const response = await axios.post(`${API_URL}/ValidationRules`, preparedRule);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la création de la règle de validation:', error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Met à jour une règle de validation
     * @param {number} id - L'ID de la règle
     * @param {Object} rule - Les données mises à jour
     * @returns {Promise<Object>} La règle mise à jour
     */
    updateRule: async (id, rule) => {
        try {
            // Préparer la règle pour l'envoi
            const preparedRule = prepareRuleForSubmission({ ...rule, id });
            console.log("Données préparées pour mise à jour:", preparedRule);

            // Assurez-vous que l'ID est défini dans l'objet envoyé
            if (!preparedRule.id) {
                preparedRule.id = id;
            }

            const response = await axios.put(`${API_URL}/ValidationRules/${id}`, preparedRule);
            return response.data;
        } catch (error) {
            console.error(`Erreur lors de la mise à jour de la règle ${id}:`, error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Supprime une règle de validation
     * @param {number} id - L'ID de la règle à supprimer
     * @returns {Promise<void>}
     */
    deleteRule: async (id) => {
        try {
            await axios.delete(`${API_URL}/ValidationRules/${id}`);
        } catch (error) {
            console.error(`Erreur lors de la suppression de la règle de validation ${id}:`, error);
            throw validationRulesService.handleError(error);
        }
    },

    /**
     * Gère les erreurs de l'API de manière standardisée
     * @param {Error} error - L'erreur survenue
     * @returns {Error} Une erreur formatée avec un message approprié
     */
    handleError: (error) => {
        if (error.response) {
            // Le serveur a répondu avec un code d'erreur
            const message = error.response.data?.message || 'Erreur serveur';
            return new Error(message);
        } else if (error.request) {
            // La requête a été envoyée mais aucune réponse n'a été reçue
            return new Error('Impossible de joindre le serveur. Vérifiez votre connexion.');
        } else {
            // Une erreur s'est produite lors de la configuration de la requête
            return new Error('Une erreur est survenue lors de la préparation de la requête.');
        }
    }
};

/**
 * Prépare une règle de validation pour l'envoi au serveur
 * @param {Object} rule - La règle à préparer
 * @returns {Object} La règle préparée
 */
function prepareRuleForSubmission(rule) {
    // Créer une copie pour éviter de modifier l'objet original
    const preparedRule = { ...rule };

    // Vérifier et formater les conditions
    if (preparedRule.conditions && Array.isArray(preparedRule.conditions)) {
        // Garder uniquement les propriétés nécessaires et les formater correctement
        preparedRule.conditions = preparedRule.conditions.map((condition, index) => {
            // Pour la première condition, s'assurer que logicalOperator est null
            if (index === 0) {
                return {
                    id: condition.id || 0, // 0 ou null pour les nouvelles conditions
                    sourceField: condition.sourceField,
                    operator: condition.operator,
                    value: ['NOTEMPTY', 'ISEMPTY'].includes(condition.operator) ? '' : (condition.value || ''),
                    logicalOperator: null
                };
            }

            // Pour les conditions suivantes
            return {
                id: condition.id || 0,
                sourceField: condition.sourceField,
                operator: condition.operator,
                value: ['NOTEMPTY', 'ISEMPTY'].includes(condition.operator) ? '' : (condition.value || ''),
                logicalOperator: condition.logicalOperator || 'AND'
            };
        });
    } else {
        // S'assurer que conditions est au moins un tableau vide
        preparedRule.conditions = [];
    }

    return preparedRule;
}

export default validationRulesService;